import React, { useState, useMemo, useCallback } from 'react';
import {
  Alert,
  Box,
  Chip,
  Paper,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// ── Try to import @xyflow/react (graceful degradation if not installed) ────────

let ReactFlowLib = null;
try {
  ReactFlowLib = require('@xyflow/react');
  require('@xyflow/react/dist/style.css');
} catch {
  // package not yet installed
}

// ── Tokenize / score ──────────────────────────────────────────────────────────

function tokenize(str) {
  return new Set((str || '').toLowerCase().split(/\W+/).filter(Boolean));
}

function scoreName(docWords, name) {
  return [...tokenize(name)].filter((w) => w.length > 3 && docWords.has(w)).length;
}

// ── Layout constants ──────────────────────────────────────────────────────────

const DOC_COL_X = 40;
const OUTCOME_COL_X = 520;
const MILESTONE_COL_X = 720;
const APP_COL_X = 940;
const FUNC_COL_X = 1140;
const DM_COL_X = 1360;
const RM_COL_X = 1580;
const DOC_ROW_GAP = 130;
const CTX_ROW_GAP = 110;
const DM_ROW_GAP = 110;
const MS_ROW_GAP = 80;

// ── Build nodes + edges ───────────────────────────────────────────────────────

function buildGraph({
  documents, context, dataMaps, roadmapNodes, onEditDoc, onFocusNode, focusedId,
  filterTypes, filterOutcomes, filterApps, searchQuery,
  dismissedEdges, onConfirmEdge, onDismissEdge, isReadOnly, showMilestones,
}) {
  const nodes = [];
  const edges = [];

  const matchSearch = (str) =>
    !searchQuery || (str || '').toLowerCase().includes(searchQuery.toLowerCase());

  const visibleDocs = documents.filter((d) => {
    if (filterTypes.length > 0 && !filterTypes.includes(d.type)) return false;
    if (filterOutcomes.length > 0 && !(d.links?.outcomes || []).some(id => filterOutcomes.includes(id))) return false;
    if (filterApps.length > 0 && !(d.links?.applications || []).some(id => filterApps.includes(id))) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (d.name || '').toLowerCase().includes(q) || (d.description || '').toLowerCase().includes(q) || (d.body || '').toLowerCase().includes(q);
    }
    return true;
  });

  // ── Step 1: Collect referenced context IDs from confirmed links ───────────
  // Maps: contextId -> link count (for sorting by frequency)
  const refOutcomes = new Map();
  const refApps = new Map();
  const refFunctions = new Map();

  visibleDocs.forEach((doc) => {
    (doc.links?.outcomes || []).forEach((id) => {
      refOutcomes.set(id, (refOutcomes.get(id) || 0) + 1);
    });
    (doc.links?.applications || []).forEach((id) => {
      refApps.set(id, (refApps.get(id) || 0) + 1);
    });
    (doc.links?.functions || []).forEach((fn) => {
      refFunctions.set(fn, (refFunctions.get(fn) || 0) + 1);
    });
  });

  // ── Step 2: Pre-compute suggested edge candidates (top 2 per doc) ─────────
  // Also add their context IDs to the referenced maps so those nodes are rendered.
  const suggestedEdges = []; // { eid, docId, nodeId }

  visibleDocs.forEach((doc) => {
    const words = tokenize(`${doc.name} ${doc.description || ''} ${doc.body || ''}`);
    const confirmed = new Set([
      ...(doc.links?.outcomes || []).map((id) => `outcome_${id}`),
      ...(doc.links?.applications || []).map((id) => `app_${id}`),
      ...(doc.links?.functions || []).map((fn) => `fn_${fn}`),
    ]);

    const candidates = [];
    (context.outcomes || []).forEach((o) => {
      if (!matchSearch(o.name)) return;
      const nid = `outcome_${o.id}`;
      const s = scoreName(words, o.name);
      if (!confirmed.has(nid) && s > 0) {
        candidates.push({ nodeId: nid, score: s, contextId: o.id, contextType: 'outcome' });
      }
    });
    (context.applications || []).forEach((a) => {
      if (!matchSearch(a.name)) return;
      const nid = `app_${a.id}`;
      const s = scoreName(words, a.name);
      if (!confirmed.has(nid) && s > 0) {
        candidates.push({ nodeId: nid, score: s, contextId: a.id, contextType: 'app' });
      }
    });
    (context.functions || []).forEach((fn) => {
      if (!matchSearch(fn)) return;
      const nid = `fn_${fn}`;
      const s = scoreName(words, fn);
      if (!confirmed.has(nid) && s > 0) {
        candidates.push({ nodeId: nid, score: s, contextId: fn, contextType: 'function' });
      }
    });

    candidates.sort((a, b) => b.score - a.score).slice(0, 2).forEach(({ nodeId, contextId, contextType }) => {
      const eid = `sugg_${doc.id}_${nodeId}`;
      if (dismissedEdges.has(eid)) return;
      suggestedEdges.push({ eid, docId: doc.id, nodeId });
      // Add to referenced maps (0.5 weight so confirmed links sort higher)
      if (contextType === 'outcome') refOutcomes.set(contextId, (refOutcomes.get(contextId) || 0) + 0.5);
      else if (contextType === 'app') refApps.set(contextId, (refApps.get(contextId) || 0) + 0.5);
      else if (contextType === 'function') refFunctions.set(contextId, (refFunctions.get(contextId) || 0) + 0.5);
    });
  });

  // ── Step 2b: Collect DM node context refs into reference maps ────────────
  (dataMaps || []).forEach((n) => {
    (n.outcome_links || []).forEach(id => refOutcomes.set(id, (refOutcomes.get(id) || 0) + 1));
    (n.app_links || []).forEach(id => refApps.set(id, (refApps.get(id) || 0) + 1));
  });

  // ── Step 2c: Collect Roadmap node context refs into reference maps ────────
  (roadmapNodes || []).forEach((n) => {
    (n.outcome_links || []).forEach(id => refOutcomes.set(id, (refOutcomes.get(id) || 0) + 1));
    (n.app_links || []).forEach(id => refApps.set(id, (refApps.get(id) || 0) + 1));
  });

  // ── Step 3: Focused-node highlight set ───────────────────────────────────
  const focusedRelated = new Set();
  if (focusedId) {
    focusedRelated.add(focusedId);
    documents.forEach((d) => {
      const allLinks = [
        ...(d.links?.outcomes || []).map((id) => `outcome_${id}`),
        ...(d.links?.applications || []).map((id) => `app_${id}`),
        ...(d.links?.functions || []).map((fn) => `fn_${fn}`),
      ];
      if (d.id === focusedId || allLinks.includes(focusedId)) {
        focusedRelated.add(d.id);
        allLinks.forEach((id) => focusedRelated.add(id));
      }
    });
    (dataMaps || []).forEach((n) => {
      const dmId = `dm_${n.id}`;
      const allLinks = [
        ...(n.doc_links || []),
        ...(n.outcome_links || []).map(id => `outcome_${id}`),
        ...(n.app_links || []).map(id => `app_${id}`),
      ];
      if (dmId === focusedId || allLinks.includes(focusedId)) {
        focusedRelated.add(dmId);
        allLinks.forEach(id => focusedRelated.add(id));
      }
    });
    (roadmapNodes || []).forEach((n) => {
      const rmId = `rm_${n.id}`;
      const allLinks = [
        ...(n.doc_links || []),
        ...(n.outcome_links || []).map(id => `outcome_${id}`),
        ...(n.app_links || []).map(id => `app_${id}`),
      ];
      if (rmId === focusedId || allLinks.includes(focusedId)) {
        focusedRelated.add(rmId);
        allLinks.forEach(id => focusedRelated.add(id));
      }
    });
  }
  const isDimmed = (id) => focusedId && !focusedRelated.has(id);

  // ── Step 4: Doc nodes (2-column grid) ────────────────────────────────────
  const docCols = 2;
  visibleDocs.forEach((doc, i) => {
    const col = i % docCols;
    const row = Math.floor(i / docCols);
    nodes.push({
      id: doc.id,
      type: 'docNode',
      position: { x: DOC_COL_X + col * 190, y: 40 + row * DOC_ROW_GAP },
      data: {
        label: doc.name,
        docType: doc.type,
        dimmed: isDimmed(doc.id),
        selected: focusedId === doc.id,
        onClick: () => onEditDoc(doc.id),
      },
    });
  });

  // ── Step 5: Context nodes (only referenced, sorted by frequency) ──────────
  const outcomeItems = [...refOutcomes.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([id]) => (context.outcomes || []).find((o) => o.id === id))
    .filter(Boolean);

  outcomeItems.forEach((outcome, i) => {
    nodes.push({
      id: `outcome_${outcome.id}`,
      type: 'outcomeNode',
      position: { x: OUTCOME_COL_X, y: 40 + i * CTX_ROW_GAP },
      data: {
        label: outcome.name,
        dimmed: isDimmed(`outcome_${outcome.id}`),
        selected: focusedId === `outcome_${outcome.id}`,
        onClick: () => onFocusNode(`outcome_${outcome.id}`),
      },
    });
  });

  // ── Step 5b: Milestone nodes (sub-column, only when showMilestones) ─────────
  if (showMilestones) {
    let msRow = 0;
    outcomeItems.forEach((outcome) => {
      const milestones = outcome.milestones || [];
      milestones.forEach((ms) => {
        const msId = `ms_${ms.id}`;
        nodes.push({
          id: msId,
          type: 'milestoneNode',
          position: { x: MILESTONE_COL_X, y: 40 + msRow * MS_ROW_GAP },
          data: {
            label: ms.name,
            startDate: ms.start_date,
            endDate: ms.end_date,
            dimmed: isDimmed(msId),
            selected: focusedId === msId,
            onClick: () => onFocusNode(msId),
          },
        });
        msRow++;
      });
    });
  }

  const appItems = [...refApps.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([id]) => (context.applications || []).find((a) => a.id === id))
    .filter(Boolean);

  appItems.forEach((app, i) => {
    nodes.push({
      id: `app_${app.id}`,
      type: 'appNode',
      position: { x: APP_COL_X, y: 40 + i * CTX_ROW_GAP },
      data: {
        label: app.name,
        dimmed: isDimmed(`app_${app.id}`),
        selected: focusedId === `app_${app.id}`,
        onClick: () => onFocusNode(`app_${app.id}`),
      },
    });
  });

  const funcItems = [...refFunctions.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([fn]) => fn);

  funcItems.forEach((fn, i) => {
    nodes.push({
      id: `fn_${fn}`,
      type: 'functionNode',
      position: { x: FUNC_COL_X, y: 40 + i * CTX_ROW_GAP },
      data: {
        label: fn,
        dimmed: isDimmed(`fn_${fn}`),
        selected: focusedId === `fn_${fn}`,
        onClick: () => onFocusNode(`fn_${fn}`),
      },
    });
  });

  // ── Step 5d: Data map nodes ───────────────────────────────────────────────
  const dmNodesSorted = [...(dataMaps || [])].sort((a, b) => {
    const aLinks = (a.doc_links?.length || 0) + (a.outcome_links?.length || 0) + (a.app_links?.length || 0);
    const bLinks = (b.doc_links?.length || 0) + (b.outcome_links?.length || 0) + (b.app_links?.length || 0);
    return bLinks - aLinks;
  });
  dmNodesSorted.forEach((n, i) => {
    const dmId = `dm_${n.id}`;
    nodes.push({
      id: dmId,
      type: 'dmNode',
      position: { x: DM_COL_X, y: 40 + i * DM_ROW_GAP },
      data: {
        label: n.label,
        mapName: n.map_name,
        dimmed: isDimmed(dmId),
        selected: focusedId === dmId,
        onClick: () => onFocusNode(dmId),
      },
    });
  });

  // ── Step 5e: Roadmap item nodes ───────────────────────────────────────────
  const rmNodesSorted = [...(roadmapNodes || [])].sort((a, b) => {
    const al = (a.doc_links?.length || 0) + (a.outcome_links?.length || 0) + (a.app_links?.length || 0);
    const bl = (b.doc_links?.length || 0) + (b.outcome_links?.length || 0) + (b.app_links?.length || 0);
    return bl - al;
  });
  rmNodesSorted.forEach((n, i) => {
    const rmId = `rm_${n.id}`;
    nodes.push({
      id: rmId,
      type: 'rmNode',
      position: { x: RM_COL_X, y: 40 + i * DM_ROW_GAP },
      data: {
        label: n.name,
        roadmapName: n.roadmap_name,
        dimmed: isDimmed(rmId),
        selected: focusedId === rmId,
        onClick: () => onFocusNode(rmId),
      },
    });
  });

  const nodeIds = new Set(nodes.map((n) => n.id));

  // ── Step 6: Confirmed edges ───────────────────────────────────────────────
  visibleDocs.forEach((doc) => {
    (doc.links?.outcomes || []).forEach((oid) => {
      if (nodeIds.has(`outcome_${oid}`)) {
        edges.push({ id: `conf_o_${doc.id}_${oid}`, source: doc.id, target: `outcome_${oid}`, type: 'confirmed' });
      }
    });
    (doc.links?.applications || []).forEach((aid) => {
      if (nodeIds.has(`app_${aid}`)) {
        edges.push({ id: `conf_a_${doc.id}_${aid}`, source: doc.id, target: `app_${aid}`, type: 'confirmed' });
      }
    });
    (doc.links?.functions || []).forEach((fn) => {
      if (nodeIds.has(`fn_${fn}`)) {
        edges.push({ id: `conf_f_${doc.id}_${fn}`, source: doc.id, target: `fn_${fn}`, type: 'confirmed' });
      }
    });
  });

  // ── Step 7: Suggested edges ───────────────────────────────────────────────
  suggestedEdges.forEach(({ eid, docId, nodeId }) => {
    if (nodeIds.has(docId) && nodeIds.has(nodeId)) {
      edges.push({
        id: eid,
        source: docId,
        target: nodeId,
        type: 'suggested',
        data: { isReadOnly, onConfirm: onConfirmEdge, onDismiss: onDismissEdge },
      });
    }
  });

  // ── Step 8: Data map node edges ───────────────────────────────────────────
  (dataMaps || []).forEach((n) => {
    const dmId = `dm_${n.id}`;
    if (!nodeIds.has(dmId)) return;
    (n.doc_links || []).forEach(docId => {
      if (nodeIds.has(docId)) edges.push({ id: `dm_d_${dmId}_${docId}`, source: dmId, target: docId, type: 'dmEdge' });
    });
    (n.outcome_links || []).forEach(oid => {
      if (nodeIds.has(`outcome_${oid}`)) edges.push({ id: `dm_o_${dmId}_${oid}`, source: dmId, target: `outcome_${oid}`, type: 'dmEdge' });
    });
    (n.app_links || []).forEach(aid => {
      if (nodeIds.has(`app_${aid}`)) edges.push({ id: `dm_a_${dmId}_${aid}`, source: dmId, target: `app_${aid}`, type: 'dmEdge' });
    });
  });

  // ── Step 9: Roadmap node edges ────────────────────────────────────────────
  (roadmapNodes || []).forEach((n) => {
    const rmId = `rm_${n.id}`;
    if (!nodeIds.has(rmId)) return;
    (n.doc_links || []).forEach((docId) => {
      if (nodeIds.has(docId)) edges.push({ id: `rm_d_${rmId}_${docId}`, source: rmId, target: docId, type: 'rmEdge' });
    });
    (n.outcome_links || []).forEach((oid) => {
      if (nodeIds.has(`outcome_${oid}`)) edges.push({ id: `rm_o_${rmId}_${oid}`, source: rmId, target: `outcome_${oid}`, type: 'rmEdge' });
    });
    (n.app_links || []).forEach((aid) => {
      if (nodeIds.has(`app_${aid}`)) edges.push({ id: `rm_a_${rmId}_${aid}`, source: rmId, target: `app_${aid}`, type: 'rmEdge' });
    });
    (n.data_map_links || []).forEach((dmNodeId) => {
      const dmId = `dm_${dmNodeId}`;
      if (nodeIds.has(dmId)) edges.push({ id: `rm_dm_${rmId}_${dmNodeId}`, source: rmId, target: dmId, type: 'rmEdge' });
    });
  });

  // ── Step 10: Milestone edges (outcome → milestone) ───────────────────────
  if (showMilestones) {
    outcomeItems.forEach((outcome) => {
      const outcomeNodeId = `outcome_${outcome.id}`;
      if (!nodeIds.has(outcomeNodeId)) return;
      (outcome.milestones || []).forEach((ms) => {
        const msId = `ms_${ms.id}`;
        if (nodeIds.has(msId)) {
          edges.push({ id: `ms_edge_${outcome.id}_${ms.id}`, source: outcomeNodeId, target: msId, type: 'msEdge' });
        }
      });
    });
  }

  return { nodes, edges };
}

// ── Legend ─────────────────────────────────────────────────────────────────────

function Legend() {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  return (
    <Paper elevation={2} sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Typography variant="caption" fontWeight={700}>Legend</Typography>
      {[
        { color: '#4db6ac', label: 'Document (rect)' },
        { color: '#42a5f5', label: 'Outcome (circle)' },
        { color: '#ffa726', label: 'Application (diamond)' },
        { color: '#ab47bc', label: 'Function (pill)' },
        { color: '#5c6bc0', label: 'Milestone' },
        { color: '#66bb6a', label: 'Data Map Node' },
        { color: '#ffa726', label: 'Roadmap Item' },
      ].map(({ color, label }) => (
        <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 12, height: 12, bgcolor: color, borderRadius: 0.5 }} />
          <Typography variant="caption">{label}</Typography>
        </Box>
      ))}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: 20, height: 2, bgcolor: dark ? '#4db6ac' : '#00897b' }} />
        <Typography variant="caption">Confirmed link</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: 20, height: 0, borderTop: '2px dashed #ffa726' }} />
        <Typography variant="caption">Suggested link</Typography>
      </Box>
    </Paper>
  );
}

// ── Inner graph (only rendered when @xyflow/react is available) ────────────────
// This component is defined lazily so hooks are never called unless the lib exists.

function makeGraphInner(rf) {
  const {
    ReactFlow, Background, Controls, MiniMap, Handle, Position,
    BaseEdge, EdgeLabelRenderer, getBezierPath, Panel,
  } = rf;

  // Custom nodes
  function DocNode({ data }) {
    const theme = useTheme();
    const dark = theme.palette.mode === 'dark';
    return (
      <Box
        onClick={data.onClick}
        sx={{
          px: 1.5, py: 1,
          bgcolor: dark ? '#0d2926' : '#e0f2f1',
          border: `2px solid ${data.selected ? (dark ? '#80cbc4' : '#00695c') : '#4db6ac'}`,
          borderRadius: 2,
          cursor: 'pointer',
          minWidth: 120, maxWidth: 155,
          opacity: data.dimmed ? 0.2 : 1,
          transition: 'opacity 0.2s',
        }}
      >
        <Handle type="source" position={Position.Right} style={{ background: '#4db6ac' }} />
        <Handle type="target" position={Position.Left} style={{ background: '#4db6ac' }} />
        <Typography variant="caption" fontWeight={600} sx={{ display: 'block', lineHeight: 1.3 }}>
          {data.label}
        </Typography>
        <Chip
          label={data.docType}
          size="small"
          sx={{ height: 14, fontSize: '0.6rem', mt: 0.5, bgcolor: dark ? '#1a3d39' : '#b2dfdb', color: dark ? '#80cbc4' : '#004d40' }}
        />
      </Box>
    );
  }

  function OutcomeNode({ data }) {
    const theme = useTheme();
    const dark = theme.palette.mode === 'dark';
    return (
      <Box
        onClick={data.onClick}
        sx={{
          width: 110, height: 110,
          bgcolor: dark ? '#0d1f3c' : '#e3f2fd',
          border: `2px solid ${data.selected ? (dark ? '#90caf9' : '#0d47a1') : '#42a5f5'}`,
          borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          textAlign: 'center',
          cursor: 'pointer',
          opacity: data.dimmed ? 0.2 : 1,
          transition: 'opacity 0.2s',
        }}
      >
        <Handle type="source" position={Position.Right} style={{ background: '#42a5f5' }} />
        <Handle type="target" position={Position.Left} style={{ background: '#42a5f5' }} />
        <Typography variant="caption" fontWeight={600} sx={{ lineHeight: 1.3, px: 0.5 }}>
          {data.label}
        </Typography>
      </Box>
    );
  }

  function AppNode({ data }) {
    const theme = useTheme();
    const dark = theme.palette.mode === 'dark';
    return (
      <Box
        onClick={data.onClick}
        sx={{
          width: 90, height: 90,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative',
          cursor: 'pointer',
          opacity: data.dimmed ? 0.2 : 1,
          transition: 'opacity 0.2s',
        }}
      >
        <Handle type="source" position={Position.Right} style={{ background: '#ff9800', zIndex: 10 }} />
        <Handle type="target" position={Position.Left} style={{ background: '#ff9800', zIndex: 10 }} />
        <Box sx={{
          position: 'absolute', inset: 0,
          bgcolor: dark ? '#2b1a00' : '#fff3e0',
          border: `2px solid ${data.selected ? (dark ? '#ffb74d' : '#e65100') : '#ffa726'}`,
          transform: 'rotate(45deg)', borderRadius: 1,
        }} />
        <Typography variant="caption" fontWeight={600}
          sx={{ position: 'relative', zIndex: 1, textAlign: 'center', px: 0.5, lineHeight: 1.3 }}>
          {data.label}
        </Typography>
      </Box>
    );
  }

  function FunctionNode({ data }) {
    const theme = useTheme();
    const dark = theme.palette.mode === 'dark';
    return (
      <Box
        onClick={data.onClick}
        sx={{
          px: 2, py: 0.75,
          bgcolor: dark ? '#1e0d2e' : '#f3e5f5',
          border: `2px solid ${data.selected ? (dark ? '#ce93d8' : '#4a148c') : '#ab47bc'}`,
          borderRadius: 3,
          cursor: 'pointer',
          opacity: data.dimmed ? 0.2 : 1,
          transition: 'opacity 0.2s',
        }}
      >
        <Handle type="source" position={Position.Right} style={{ background: '#ab47bc' }} />
        <Handle type="target" position={Position.Left} style={{ background: '#ab47bc' }} />
        <Typography variant="caption" fontWeight={600}>{data.label}</Typography>
      </Box>
    );
  }

  // Custom edges
  function SuggestedEdge({ id, sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, data }) {
    const theme = useTheme();
    const dark = theme.palette.mode === 'dark';
    const [path, labelX, labelY] = getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });
    return (
      <>
        <BaseEdge id={id} path={path} style={{ stroke: '#ffa726', strokeDasharray: '6,3', strokeWidth: 1.5 }} />
        {data?.onConfirm && !data?.isReadOnly && (
          <EdgeLabelRenderer>
            <Box sx={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              display: 'flex', gap: 0.25, pointerEvents: 'all',
            }}>
              <Box component="button" onClick={() => data.onConfirm(id)} sx={{
                border: '1px solid #ffa726',
                bgcolor: dark ? '#3a2800' : '#fff8e1',
                color: dark ? '#ffb74d' : '#e65100',
                borderRadius: '50%', width: 18, height: 18, fontSize: 10, cursor: 'pointer',
                '&:hover': { bgcolor: dark ? '#5a3d00' : '#ffe082' },
              }}>✓</Box>
              <Box component="button" onClick={() => data.onDismiss(id)} sx={{
                border: `1px solid ${dark ? '#616161' : '#bdbdbd'}`,
                bgcolor: dark ? '#2a2a2a' : '#fafafa',
                color: dark ? '#9e9e9e' : '#757575',
                borderRadius: '50%', width: 18, height: 18, fontSize: 10, cursor: 'pointer',
                '&:hover': { bgcolor: dark ? '#3a3a3a' : '#eeeeee' },
              }}>✕</Box>
            </Box>
          </EdgeLabelRenderer>
        )}
      </>
    );
  }

  function ConfirmedEdge({ id, sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition }) {
    const theme = useTheme();
    const dark = theme.palette.mode === 'dark';
    const [path] = getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });
    return <BaseEdge id={id} path={path} style={{ stroke: dark ? '#4db6ac' : '#00897b', strokeWidth: 2 }} />;
  }

  function DataMapNode({ data }) {
    const theme = useTheme();
    const dark = theme.palette.mode === 'dark';
    return (
      <Box
        onClick={data.onClick}
        sx={{
          px: 1.5, py: 1, minWidth: 120, maxWidth: 160,
          bgcolor: dark ? '#0d2316' : '#e8f5e9',
          border: `2px solid ${data.selected ? (dark ? '#81c784' : '#2e7d32') : '#66bb6a'}`,
          borderRadius: 1.5, cursor: 'pointer',
          opacity: data.dimmed ? 0.2 : 1, transition: 'opacity 0.2s',
        }}
      >
        <Handle type="source" position={Position.Right} style={{ background: '#66bb6a' }} />
        <Handle type="target" position={Position.Left} style={{ background: '#66bb6a' }} />
        <Typography variant="caption" fontWeight={600} sx={{ display: 'block', lineHeight: 1.3 }}>
          {data.label}
        </Typography>
        <Chip
          label={data.mapName}
          size="small"
          sx={{ height: 14, fontSize: '0.6rem', mt: 0.5, bgcolor: dark ? '#1a3d22' : '#c8e6c9', color: dark ? '#81c784' : '#1b5e20' }}
        />
      </Box>
    );
  }

  function DmEdge({ id, sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition }) {
    const [path] = getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });
    return <BaseEdge id={id} path={path} style={{ stroke: '#66bb6a', strokeWidth: 1.5, strokeDasharray: '4,3' }} />;
  }

  function RoadmapItemNode({ data }) {
    const theme = useTheme();
    const dark = theme.palette.mode === 'dark';
    return (
      <Box
        onClick={data.onClick}
        sx={{
          px: 1.5, py: 1, minWidth: 120, maxWidth: 160,
          bgcolor: dark ? '#2b1a00' : '#fff3e0',
          border: `2px solid ${data.selected ? (dark ? '#ffb74d' : '#e65100') : '#ffa726'}`,
          borderRadius: 1.5, cursor: 'pointer',
          opacity: data.dimmed ? 0.2 : 1, transition: 'opacity 0.2s',
        }}
      >
        <Handle type="source" position={Position.Right} style={{ background: '#ffa726' }} />
        <Handle type="target" position={Position.Left} style={{ background: '#ffa726' }} />
        <Typography variant="caption" fontWeight={600} sx={{ display: 'block', lineHeight: 1.3 }}>
          {data.label}
        </Typography>
        {data.roadmapName && (
          <Chip
            label={data.roadmapName}
            size="small"
            sx={{ height: 14, fontSize: '0.6rem', mt: 0.5, bgcolor: dark ? '#3a2800' : '#ffe0b2', color: dark ? '#ffb74d' : '#bf360c' }}
          />
        )}
      </Box>
    );
  }

  function RmEdge({ id, sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition }) {
    const [path] = getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });
    return <BaseEdge id={id} path={path} style={{ stroke: '#ffa726', strokeWidth: 1.5 }} />;
  }

  function MilestoneNode({ data }) {
    const theme = useTheme();
    const dark = theme.palette.mode === 'dark';
    return (
      <Box
        onClick={data.onClick}
        sx={{
          px: 1.25, py: 0.75, minWidth: 110, maxWidth: 155,
          bgcolor: dark ? '#0d1a2e' : '#e8eaf6',
          border: `2px solid ${data.selected ? (dark ? '#7986cb' : '#283593') : '#5c6bc0'}`,
          borderRadius: 1, cursor: 'pointer',
          opacity: data.dimmed ? 0.2 : 1, transition: 'opacity 0.2s',
        }}
      >
        <Handle type="source" position={Position.Right} style={{ background: '#5c6bc0' }} />
        <Handle type="target" position={Position.Left} style={{ background: '#5c6bc0' }} />
        <Typography variant="caption" fontWeight={600} sx={{ display: 'block', lineHeight: 1.3, fontSize: '0.65rem' }}>
          {data.label}
        </Typography>
        {(data.startDate || data.endDate) && (
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.58rem', display: 'block', mt: 0.25 }}>
            {data.startDate}{data.startDate && data.endDate ? ' → ' : ''}{data.endDate}
          </Typography>
        )}
      </Box>
    );
  }

  function MsEdge({ id, sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition }) {
    const [path] = getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });
    return <BaseEdge id={id} path={path} style={{ stroke: '#5c6bc0', strokeWidth: 1.5, strokeDasharray: '3,3' }} />;
  }

  const NODE_TYPES = { docNode: DocNode, outcomeNode: OutcomeNode, appNode: AppNode, functionNode: FunctionNode, dmNode: DataMapNode, rmNode: RoadmapItemNode, milestoneNode: MilestoneNode };
  const EDGE_TYPES = { confirmed: ConfirmedEdge, suggested: SuggestedEdge, dmEdge: DmEdge, rmEdge: RmEdge, msEdge: MsEdge };

  // The actual graph component (hooks always called — no conditional hook calls)
  return function GraphInner({ documents, context, dataMaps, roadmapNodes, isReadOnly, onEditDoc, onUpdateDoc,
                               filterTypes = [], filterOutcomes = [], filterApps = [], searchQuery = '' }) {
    const [focusedId, setFocusedId] = useState(null);
    const [dismissedEdges, setDismissedEdges] = useState(new Set());
    const [dmInfoDismissed, setDmInfoDismissed] = useState(false);
    const [showMilestones, setShowMilestones] = useState(false);

    const handleFocusNode = useCallback((id) => {
      setFocusedId((prev) => (prev === id ? null : id));
    }, []);

    const handleConfirmEdge = useCallback(
      async (edgeId) => {
        // edgeId format: sugg_{docId}_{targetNodeId}
        // docId is always a 36-char UUID
        const withoutPrefix = edgeId.slice('sugg_'.length);
        const docId = withoutPrefix.slice(0, 36);
        const targetNodeId = withoutPrefix.slice(37); // skip underscore

        let category, linkId;
        if (targetNodeId.startsWith('outcome_')) { category = 'outcomes'; linkId = targetNodeId.replace('outcome_', ''); }
        else if (targetNodeId.startsWith('app_')) { category = 'applications'; linkId = targetNodeId.replace('app_', ''); }
        else if (targetNodeId.startsWith('fn_')) { category = 'functions'; linkId = targetNodeId.replace('fn_', ''); }
        if (!category) return;

        const doc = documents.find((d) => d.id === docId);
        if (!doc) return;

        const updatedLinks = { ...doc.links, [category]: [...(doc.links[category] || []), linkId] };
        try {
          const res = await fetch(`${API_BASE}/api/doc-hub/${doc.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ links: updatedLinks }),
          });
          if (!res.ok) return;
          onUpdateDoc(await res.json());
          setDismissedEdges((prev) => new Set([...prev, edgeId]));
        } catch { /* ignore */ }
      },
      [documents, onUpdateDoc]
    );

    const handleDismissEdge = useCallback((edgeId) => {
      setDismissedEdges((prev) => new Set([...prev, edgeId]));
    }, []);

    const { nodes, edges } = useMemo(
      () =>
        buildGraph({
          documents, context, dataMaps, roadmapNodes, onEditDoc,
          onFocusNode: handleFocusNode,
          focusedId, filterTypes, filterOutcomes, filterApps, searchQuery,
          dismissedEdges,
          onConfirmEdge: handleConfirmEdge,
          onDismissEdge: handleDismissEdge,
          isReadOnly, showMilestones,
        }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [documents, context, dataMaps, roadmapNodes, focusedId, filterTypes, filterOutcomes, filterApps, searchQuery, dismissedEdges, isReadOnly, showMilestones]
    );

    // Stats derived from built nodes
    const docCount = nodes.filter((n) => n.type === 'docNode').length;
    const outcomeCount = nodes.filter((n) => n.type === 'outcomeNode').length;
    const appCount = nodes.filter((n) => n.type === 'appNode').length;
    const funcCount = nodes.filter((n) => n.type === 'functionNode').length;
    const dmCount = nodes.filter((n) => n.type === 'dmNode').length;
    const rmCount = nodes.filter((n) => n.type === 'rmNode').length;
    const msCount = nodes.filter((n) => n.type === 'milestoneNode').length;
    const hasMilestones = (context.outcomes || []).some((o) => (o.milestones || []).length > 0);

    // App reverse-lookup: outcomes and docs linked to the focused application
    const focusedAppId = focusedId?.startsWith('app_') ? focusedId.replace('app_', '') : null;
    const focusedApp = focusedAppId ? (context.applications || []).find((a) => a.id === focusedAppId) : null;
    const appLinkedOutcomes = focusedAppId
      ? (context.outcomes || []).filter((o) =>
          (o.applications || []).some((a) => (typeof a === 'string' ? a : a.id) === focusedAppId)
        )
      : [];
    const appLinkedDocs = focusedAppId
      ? documents.filter((d) => (d.links?.applications || []).includes(focusedAppId))
      : [];

    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

        {/* Stats bar */}
        <Box sx={{
          px: 2, py: 0.5,
          display: 'flex', alignItems: 'center', gap: 2,
          bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider',
          flexShrink: 0,
        }}>
          <Typography variant="caption" color="text.secondary">
            {docCount} {docCount === 1 ? 'doc' : 'docs'}
            {outcomeCount > 0 && ` · ${outcomeCount} ${outcomeCount === 1 ? 'outcome' : 'outcomes'}`}
            {showMilestones && msCount > 0 && ` · ${msCount} ${msCount === 1 ? 'milestone' : 'milestones'}`}
            {appCount > 0 && ` · ${appCount} ${appCount === 1 ? 'app' : 'apps'}`}
            {funcCount > 0 && ` · ${funcCount} ${funcCount === 1 ? 'function' : 'functions'}`}
            {dmCount > 0 && ` · ${dmCount} map ${dmCount === 1 ? 'node' : 'nodes'}`}
            {rmCount > 0 && ` · ${rmCount} roadmap ${rmCount === 1 ? 'item' : 'items'}`}
          </Typography>
          {hasMilestones && (
            <Box
              component="button"
              onClick={() => setShowMilestones((v) => !v)}
              sx={{
                border: showMilestones ? '1px solid #5c6bc0' : `1px solid`,
                borderColor: showMilestones ? '#5c6bc0' : 'divider',
                bgcolor: showMilestones ? '#e8eaf6' : 'transparent',
                color: showMilestones ? '#283593' : 'text.secondary',
                borderRadius: 1, px: 1, py: 0.25,
                fontSize: '0.7rem', cursor: 'pointer',
                '&:hover': { borderColor: '#5c6bc0', color: '#283593' },
              }}
            >
              {showMilestones ? 'Hide Milestones' : 'Show Milestones'}
            </Box>
          )}
          {focusedId && (
            <Box
              component="button"
              onClick={() => setFocusedId(null)}
              sx={{
                border: 'none', bgcolor: 'transparent', color: 'primary.main',
                fontSize: '0.72rem', cursor: 'pointer', p: 0,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              × Clear focus
            </Box>
          )}
        </Box>

        {/* Data map link hint */}
        {!dmInfoDismissed && (dataMaps || []).length === 0 && (
          <Alert
            severity="info"
            onClose={() => setDmInfoDismissed(true)}
            sx={{ borderRadius: 0, py: 0.25, fontSize: '0.72rem' }}
          >
            Link data map nodes to docs or outcomes — they'll appear here when connected.
          </Alert>
        )}

        {/* Graph area */}
        <Box sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
          {docCount === 0 && dmCount === 0 ? (
            <Box sx={{
              height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexDirection: 'column', gap: 1,
            }}>
              <Typography color="text.secondary">
                {(filterTypes.length > 0 || filterOutcomes.length > 0 || filterApps.length > 0 || searchQuery)
                  ? 'No documents match your filters.'
                  : 'No documents yet. Create documents in the list view and link them to outcomes, apps, and functions.'}
              </Typography>
            </Box>
          ) : (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={NODE_TYPES}
              edgeTypes={EDGE_TYPES}
              onPaneClick={() => setFocusedId(null)}
              fitView
              fitViewOptions={{ padding: 0.2 }}
              minZoom={0.3}
              maxZoom={2}
            >
              <Background />
              <Controls />
              <MiniMap nodeColor={(n) => {
                const c = { docNode: '#4db6ac', outcomeNode: '#42a5f5', appNode: '#ffa726', functionNode: '#ab47bc', dmNode: '#66bb6a' };
                return c[n.type] || '#ccc';
              }} />

              {/* Legend — uses Panel to avoid overlapping Controls */}
              <Panel position="top-left">
                <Legend />
              </Panel>

              {/* Application reverse-lookup panel */}
              {focusedApp && (
                <Panel position="top-right">
                  <Paper
                    elevation={4}
                    sx={{ p: 1.5, width: 220, display: 'flex', flexDirection: 'column', gap: 1 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: '#ffa726', flexShrink: 0 }} />
                      <Typography variant="caption" fontWeight={700} sx={{ flex: 1 }}>
                        {focusedApp.name}
                      </Typography>
                      <Box
                        component="button"
                        onClick={() => setFocusedId(null)}
                        sx={{
                          border: 'none', bgcolor: 'transparent', cursor: 'pointer', p: 0,
                          color: 'text.secondary', '&:hover': { color: 'text.primary' },
                        }}
                      >
                        ✕
                      </Box>
                    </Box>
                    {appLinkedOutcomes.length > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                          Outcomes ({appLinkedOutcomes.length})
                        </Typography>
                        {appLinkedOutcomes.slice(0, 5).map((o) => (
                          <Box key={o.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#42a5f5', flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ lineHeight: 1.3 }}>{o.name}</Typography>
                          </Box>
                        ))}
                        {appLinkedOutcomes.length > 5 && (
                          <Typography variant="caption" color="text.disabled">+{appLinkedOutcomes.length - 5} more</Typography>
                        )}
                      </Box>
                    )}
                    {appLinkedDocs.length > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                          Documents ({appLinkedDocs.length})
                        </Typography>
                        {appLinkedDocs.slice(0, 4).map((d) => (
                          <Box key={d.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                            <Box sx={{ width: 5, height: 5, borderRadius: 0.5, bgcolor: '#4db6ac', flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ lineHeight: 1.3 }}>{d.name}</Typography>
                          </Box>
                        ))}
                        {appLinkedDocs.length > 4 && (
                          <Typography variant="caption" color="text.disabled">+{appLinkedDocs.length - 4} more</Typography>
                        )}
                      </Box>
                    )}
                    {appLinkedOutcomes.length === 0 && appLinkedDocs.length === 0 && (
                      <Typography variant="caption" color="text.disabled">No linked outcomes or documents.</Typography>
                    )}
                  </Paper>
                </Panel>
              )}

              {/* Click-to-focus hint */}
              {!focusedId && (
                <Panel position="bottom-right">
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.disabled', pointerEvents: 'none', mb: 1, display: 'block' }}
                  >
                    Click any node to highlight its connections
                  </Typography>
                </Panel>
              )}
            </ReactFlow>
          )}
        </Box>
      </Box>
    );
  };
}

// ── Public component ──────────────────────────────────────────────────────────

// Instantiate the inner component once (not on every render)
const GraphInner = ReactFlowLib ? makeGraphInner(ReactFlowLib) : null;

function DocGraph(props) {
  if (!GraphInner) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary" gutterBottom>
          Graph view requires <code>@xyflow/react</code>.
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Run <code>npm install</code> in the <code>frontend/</code> directory to enable this feature.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 260px)', minHeight: 500 }}>
      <GraphInner {...props} />
    </Box>
  );
}

export default DocGraph;
